/**
 *  @file       dcp-timer.js
 *              - Standard JS timer functions, with .ref and .unref properties, safe for use in browsers, 
 *                pythonmonkey and Node.js without feature-testing.
 *              - Extras related to timers / event loops
 *
 *  @author     Wes Garland, wes@kingsds.network
 *  @date       Dec 2019
 */
'use strict';

const { assert } = require('./dcp-assert');
const debugging = require('../debugging').scope('timers');
const __debugging = debugging;
const env = require('dcp/common/dcp-env');
let timerSeq = 0;

/**
 * setImmediate, but for a specific number of event loop passes.
 * @param {function} callback   The callback to run
 * @param {number}   N          The number of event loop passes to wait before running callback
 */
exports.setImmediateN = function setImmediateN(callback, N)
{
  switch(N)
  {
    case 0:
      process.nextTick(callback);
      break;
    case 1:
      setImmediate(callback);
      break;
    default:
    N = Math.max(N-1 || 0, 0);
    setImmediate(() => setImmediateN(callback, N));
    break;
  }
}

/**
 * Sets a normal timeout, but delayed by an additional 1ms to avoid firing too
 * early in Node.js. This works around rounding issues from Node.js' underlying
 * timing units not being precise enough. See
 * https://github.com/nodejs/node/issues/26578 for more information.
 */
function setTimeoutDelayed(callback, delay, ...args)
{
  return setTimeout(callback, delay + 1, ...args);
}

/**
 *  These timers are implemented in the browser but refs/unrefs are not apart of the API, so we wrap the timers with our own 
 *  implementation which contain refs if we can't detect them in the current environment
 */
exports.setTimeout = env.isReferencedTimerPlatform ? setTimeoutDelayed : function dcpTimers$$setTimeout(code, delay)
{
  const hnd = {
    id: setTimeout(...arguments),
    unref: () => hnd,
    ref:   () => hnd,
  };
  debugging() && console.debug(`new timeout ${hnd.id}, fires in ${delay}ms, callback is`,
                               typeof code !== 'function' ? code : code.name || String(code));
  return hnd;
};

exports.setInterval = env.isReferencedTimerPlatform ? setInterval : function dcpTimers$$setInterval(code, delay)
{
  const hnd = {
    id: setInterval(...arguments),
    unref: () => hnd,
    ref:   () => hnd,
  };
  debugging() && console.debug(`new interval ${hnd.id}, fires every ${delay}ms, callback is`,
                               typeof code !== 'function' ? code : code.name || String(code));
  return hnd;
};

exports.clearTimeout = env.isReferencedTimerPlatform ? clearTimeout : function dcpTimers$$clearTimeout(hnd) {
  debugging() && console.debug(`clear timeout ${hnd?.id}`);
  clearTimeout(hnd?.id || hnd);
};

exports.clearInterval = env.isReferencedTimerPlatform ? clearInterval : function dcpTimers$$clearInterval(hnd) {
  if (hnd === 'undefined')
    console.error(new Error());
  debugging() && console.debug(`clear interval ${hnd?.id}`);
  clearInterval(hnd?.id || hnd);
};

/**
 * setImmediate/clearImmediate are not implemented in browsers
 */
if (typeof setImmediate !== 'function')
  require('setimmediate');

exports.setImmediate = setImmediate;
exports.clearImmediate = clearImmediate;

/**
 * Exponential backoff function similar to IEEE 802.3 CSMA/CD standard in that it
 * chooses randomly from an exponentially growing pool of values. Also supports truncating
 * at a max value and starting the range at slot instead of 0.
 *
 * This system will never generate a number greater than max. That means if max falls between
 * two ranges, the smaller will be chosen. For example if the first range is [0,4] and the next is [0,8]
 * and the max is 7, then numbers will be drawn from [0,4] and never grow to the [0,8] range.
 *
 * @param {number} slot All values returned by this will be an integer multiple of the slot value.
 * @param {number} [max] Truncate exponential growth at this value if provided.
 * @param {number} [base=2] Exponential growth factor (must be greater than 1 for truncation to work).
 * @param {boolean} [includeZero=true] Usually 0 is a possible value. If we exclude it then we always
 *                                     return a value of at least `slot`.
 * @example - slot is 5, no other defaults used.
 * const backoff = makeEBOIterator(5);
 * backoff.next().value // chosen randomly from: [0, 5]
 * backoff.next().value // chosen randomly from: [0, 5, 10, 15] = [0, slot * 3]
 * backoff.next().value // chosen randomly from: [0, 5, ... 30, 35] = [0, slot * 7] = [0, slot * 2^iteration-1]
 *
 ************************************************************************
 * Docs from Wes which attempt to summarize the above but might be wrong:
 * - this function is an ES6 function* generator, you can use it as the iterator in a loop, and every
 *   time it will return a probably-different value
 * - the returned value, on average, will grow over time, but has lots of up/down entropy so that if it is used
 *   as the backoff for a network service with lots of clients we don't get weird feedback effects
 * - the return value will never be bigger than max; specify max=null for infinite growth
 * - the return value will be an even multiple of slot
 * - the return value grows slower for small slot
 * - the return value grows way faster with big base (2 is a good number)
 * - if you want a bunch of short retries at start, use a small slot
 * - experimental data suggests that the first return value will always be 0 or slot
 * - makeEBOIterator(0.2, 60, 1.7) seems to work pretty well for normal network service retries
 *
 * Advice from Wes: do not use this function.
 */
exports.makeEBOIterator = function* dcpTimer$$makeEBOIterator(slot, max=null, base=2) {
  let truncated = false;
  let iteration = 1;
  let maxSlots, k, value, maxVal;

  while (true) {
    if (!truncated) {
      maxSlots = Math.pow(base, iteration);
      maxVal = maxSlots * slot;
      truncated = max && maxVal >= max;
      // if we overshot the max
      if (maxVal > max) maxSlots = Math.pow(base, --iteration);
      if (!truncated) iteration++;
    }

    k = Math.floor(Math.random() * maxSlots);
    value = slot * k;
    yield value;
  }
}

/** Exponential backoff timer. Works like setInterval, except it has an exponential
 *  delay, described by the timerConfig object.
 *
 *  @param     fun          {object}   The function to call every interval
 *  @param     timerConfig  {object}   An object describing the backoff rate:
 *                                     .maxInterval   - the longest possible interval to wait
 *                                     .baseInterval  - the shortest possible interval to wait
 *                                     .backoffFactor - the rate at which we move from base to max.
 *  @returns a timer id suitable for use with clearBackoffInterval
 */
exports.setBackoffInterval = function dcpTimer$$setBackoffInterval(
  fun,
  timerConfig,
) {
  if (typeof timerConfig === 'undefined' || timerConfig === null) {
    throw new TypeError(
      `Timer configuration (${timerConfig}) cannot be undefined or null.`,
    );
  }

  const timerHnd = { id: ++timerSeq };
  let n = 0;
  let isUnref = false;

  debugging('backoff') && console.log('timers - creating backoff timer', timerHnd.id, timerConfig.label || '');

  const eboIterator = exports.makeEBOIterator(timerConfig.baseInterval, timerConfig.maxInterval, timerConfig.backoffFactor);

  function backoffIntervalWrapper() {
    if (timerHnd.currentTimeoutId === 'cancel')
      return;
    const nextInterval = eboIterator.next().value;
    debugging('backoff') && console.log(`timers - servicing backoff timer ${timerHnd.id} with ${fun.name || 'unnamed callback function'}; nextInterval is ${Math.floor(nextInterval)}ms`);
    fun(nextInterval);
    if (timerHnd.currentTimeoutId !== 'cancel') {
      timerHnd.currentTimeoutId = exports.setTimeout(backoffIntervalWrapper, nextInterval);
      if (isUnref)
        timerHnd.unref();
    }
  };

  exports.setImmediate(backoffIntervalWrapper).unref;
  timerHnd.unref = function() {
    isUnref = true;
    if (timerHnd.currentTimeoutId)
      timerHnd.currentTimeoutId.unref();
  };
  return timerHnd;
}

/** Clear a backoff interval - prevent the timer from running.
 *  @param   timerHnd    A timer handle returned from setBackoffInterval
 */
exports.clearBackoffInterval = function dcpTimer$$clearBackoffInterval(timerHnd) {
  if (timerHnd?.currentTimeoutId && timerHnd.currentTimeoutId !== 'cancel') {
    clearTimeout(timerHnd.currentTimeoutId);
    timerHnd.currentTimeoutId = 'cancel';
  }
}

/**
 * Registry that contains information about the current timeouts from
 * dcpSetTimeout. Each object is removed from the array after the
 * timeout has completed.
 *
 * This takes the form of an array of objects where each object has
 * four properties:
 *   delay                MS before callback is executed
 *   initialTime          MS Unix time dcpSetInterval was called
 *   initalTimePlusDelay  MS Unix time callback can be executed
 *   callback             The callback function to be execuetd
 */
exports.timeoutRegistry = [];

/**
 * Wrapper for setTimeout that contains the entire error stack when
 * an error is thrown in the callback and tracks timeouts in a registry.
 *
 * @param    callback    Function that executes after the delay
 * @param    delay       Delay in milliseconds
 * @param    ...args     Parameters that are passed into the callback
 */
exports.dcpSetTimeout = function dcpTimer$$dcpSetTimeout(callback, delay, ...args) {
  // add an entry for this timeout into the registry (all in MS)
  let registry = exports.timeoutRegistry ;
  let currentDate = Date.now();
  const timeoutEntry = {
    delay,
    initialTime:          Date(currentDate),
    initialTimePlusDelay: Date(currentDate += delay),
    callback,
  };
  registry.push(timeoutEntry);

  // capture the current stack and add it to the error if callback throws
  const outerStack = (new Error()).stack;

  exports.setTimeout(() => {
    // remove from the registry
    registry.splice(registry.indexOf(timeoutEntry), 1);

    try {
      callback.apply(null, args);
    } catch (error) {
      if (outerStack && error.stack)
        error.stack = error.stack.concat(outerStack);
      throw error;
    }
  }, delay);
}

/**
 * Utility class for implementing event loop references. A single "real" reference is held and
 * reference-counted, per class. This happens by adding an __elRefs internal property on
 * this.constructor (aka "static property of the class") when the constructor runs (eg via super).
 *
 * @constructor
 * @param {function} debugging      DCP_DEBUG handle for the class
 * @param {function} getDebugLabel  a function which returns the current debug label for the instance
 */
exports.Reference = function dcpTimers$$Reference(debugging, getDebugLabel)
{
  if (this.constructor.hasOwnProperty('__elRefs'))
    return;

  if (!debugging)
    debugging = __debugging;

  debugging('refs') && console.debug('! subclassed Reference as:', this.constructor.name);
  const refsMemo = this.constructor.__elRefs = {
    count: 0,                                   /* how many refs for instances of this class */
    timerHnd: null,                             /* if count > 0, a reference to node's event loop */
    debugging,                                  /* DCP debugging function pointer */
    getDebugLabel,                              /* debugLabel getter function pointer */
    seq: (exports.Reference.seq || 0) + 1,      /* subclass sequence number */
    these: new Set()                            /* list of instances with active references */
  };
  exports.Reference.seq = refsMemo.seq;

  if (env.platform === 'nodejs' && debugging('refs') && !debugging('all'))
  {
    refsMemo.these = new Set();
    process.on('SIGQUIT', () => {
      const timerHnd = refsMemo.timerHnd;
      const label = getDebugLabel ? getDebugLabel() + ' ' : '';

      console.log('\r' + label + '--- Reference Subclass:', this.constructor.name);
      console.log(label + '. seq:  ', refsMemo.seq);
      console.log(label + '. timer:', timerHnd && (timerHnd._onTimeout?.name || String(timerHnd._onTimeout)));
      console.log(label + '. refs: ', refsMemo.count);
      console.log(label + '. these:', Array.from(refsMemo.these.keys()));
      console.log();
    });
  }
}

/**
 * Add a reference-counted event loop reference
 */
exports.Reference.prototype.addReference = function dcpTimers$$addReference()
{
  const refsMemo = this.constructor.__elRefs;
  const label = refsMemo.getDebugLabel ? refsMemo.getDebugLabel() + ' ' : '';

  refsMemo.these.add(this);
  refsMemo.count++;

  if (!refsMemo.timerHnd)
    refsMemo.timerHnd = setInterval(() => 1, 1800668.6868);

  refsMemo.debugging('refs') && console.debug(label + '+', this.constructor.name, this.id || '', 'refs =', refsMemo.count);
}

/**
 * Remove a reference-counted event loop reference
 */
exports.Reference.prototype.removeReference = function dcpTimers$$removeReference()
{
  const refsMemo = this.constructor.__elRefs;
  const label = refsMemo.getDebugLabel ? refsMemo.getDebugLabel() + ' ' : '';

  refsMemo.these.delete(this);
  refsMemo.count--;
  assert(refsMemo.count >= 0);
  if (refsMemo.count < 0)
    refsMemo.count = 0;

  if (!(refsMemo.count > 0))
  {
    clearInterval(refsMemo.timerHnd);
    refsMemo.timerHnd = false;
  }
  refsMemo.debugging('refs') && console.log(label + '-', this.constructor.name, this.id || '', 'refs =', refsMemo.count);
}

/**
 * Add a ref/unref-style reference on this instance.
 */
exports.Reference.prototype.ref = function dcpTimers$$util$$ref()
{
  if (!this.hasOwnProperty('__refed'))
    this.__refed = false;

  if (!this.__refed)
  {
    this.addReference();
    this.__refed = true;
  }
  return this;
}

/**
 * Remove the ref/unref-style for this instance.
 */
exports.Reference.prototype.unref = function dcpTimers$$util$$unref()
{
  if (!this.hasOwnProperty('__refed'))
    this.__refed = false;

  if (this.__refed)
  {
    this.removeReference();
    this.__refed = false;
  }
  return this;
}

/**
 * - returns true iff this object has a direct event loop reference
 * - returns false iff this object has been explictly unref'd
 * - returns undefined if this object doesn't have a reference but neither ref nor unref has been used
 */
exports.Reference.prototype.hasRef = function dcpTimers$$util$$hasRef()
{
  return this.__refed;
}
